import { useQuery } from '@tanstack/react-query';
import { Endpoints } from '@xver/apis';
import type { QuotaUsage } from '@xver/utils/quota';
import { useActiveProjectScope } from './useActiveProject';

export const QUOTAS_QUERY_KEY = ['apis.v1.quotas'];

interface Quotas {
    instances?: QuotaUsage;
    sshKeys?: QuotaUsage;
}

// ponytail: rows are matched on the English `text` the API hard-codes
// (xver-api internal/pkg/api/qouta.go). Swap to a stable `key` field if the
// backend ever gains one.
export function useQuotasQuery() {
    const { activeProjectId, isResolved } = useActiveProjectScope();

    const { data, isPending, refetch } = useQuery({
        queryKey: [...QUOTAS_QUERY_KEY, activeProjectId],
        queryFn: Endpoints.v1.Quotas.getQuotas,
        staleTime: 1000 * 30,
        retry: 2,
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
        enabled: isResolved,
    });

    const pick = (text: string): QuotaUsage | undefined => {
        const row = data?.find((quota) => quota.text === text);
        return row && { current: row.current, limit: row.limit };
    };

    const quotas: Quotas = {
        instances: pick('Instances'),
        sshKeys: pick('SSH Keys'),
    };

    return { ...quotas, isPending, refetch };
}
